import { useAppSelector } from '@core/hooks/redux-hooks';
import { FC, Fragment, useState } from 'react';
import { ViewChangeType } from '@app/shared/models/contracts/enums/shared-enums';
import { useLocale } from '@app/core/hooks/useLocale';
import SvgAnalysis from '@app/shared/icons/Analysis';
import styles from './opm-inputs-sln-and-pref-share-section.module.scss';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { CorkscrewModal } from './CorkscrewModal';
import { compareDeep } from '@app/shared/helpers/clone/compare-deep';
import {
  OpmInputsYearDebtInstrumentCalculationResultsPeriodByEventDto,
  OpmInputsYearDebtInstrumentCalculationResultsPeriodTotalDto,
  OpmInputsYearDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { ShareSectionTableRow } from './ShareSectionTableRow';
import { mapCouponTypeInterestToTranslationWithHover } from '@app/shared/utils/CouponInterestTranslationHelper';
import {
  filterHistoricEventDatesFromFirstEvent,
  getMaxColumnCount,
} from '../../../utils/OpmInputHelper';
import OpmInputHistoricCells from '../../OpmInputHistoricCells';

export const OpmInputsSlnAndPrefShareSectionERF: FC = () => {
  const { l } = useLocale();
  const project = useAppSelector((state) => state.project.projectDraft);
  const opmInputResults = useAppSelector((state) => state.opmCalculation.calculatedInputs);
  const opmInput = { ...project.opmInput };
  const numberOfInputForecastYears = Object.keys(opmInputResults?.forecastDates).length ?? 0;
  const caseForecasts = Object.entries(opmInputResults?.forecastDates).slice(
    0,
    numberOfInputForecastYears
  );

  const [isCorkscrewModalOpen, setIsCorkscrewModalOpen] = useState(false);
  const [instrumentId, setInstrumentId] = useState('');
  const [instrumentNarrative, setInstrumentNarrative] = useState('');
  const [eventData, setEventData] = useState<
    OpmInputsYearDebtInstrumentCalculationResultsPeriodByEventDto[]
  >([]);
  const [shouldHidePrevious, setShouldHidePrevious] = useState(true);
  const [shouldHideNext, setShouldHideNext] = useState(true);
  const filteredHistoricYears = filterHistoricEventDatesFromFirstEvent(
    opmInputResults.historicEventDates
  );
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.opmInputs.showHistoricDataColumns
  );

  const sectionColCount = getMaxColumnCount(
    Object.keys(filteredHistoricYears).length,
    opmInput?.perYearInputs.length ?? 0,
    showHistoricDataColumns
  );

  const determineCorkscrewNavigationButtonVisibility = (instrumentId: string, date: string) => {
    const allDates = [];

    allDates.push(project.valuationDate);

    const forecastDates = Object.keys(opmInputResults.forecastDates);
    allDates.push(...forecastDates);

    const hasMultipleEvents = (date: string) => {
      if (forecastDates.includes(date)) {
        const forecastEvents =
          opmInputResults?.forecastDates?.[date]?.shareholderDebtInstrumentsEvents?.[instrumentId]
            ?.periodByEvent;
        return forecastEvents ? forecastEvents.length > 1 : false;
      } else {
        const valuationEvents =
          opmInputResults?.valuationDate?.shareholderDebtInstrumentsEvents?.[instrumentId]
            ?.periodByEvent;
        return valuationEvents ? valuationEvents.length > 1 : false;
      }
    };

    const firstDateWithMultipleEvents = allDates.find((date) => hasMultipleEvents(date));
    const lastDateWithMultipleEvents = allDates
      .slice()
      .reverse()
      .find((date) => hasMultipleEvents(date));

    const isFirstDateWithMultipleEvents = date === firstDateWithMultipleEvents;
    const isLastDateWithMultipleEvents = date === lastDateWithMultipleEvents;

    return {
      shouldHidePrevious: isFirstDateWithMultipleEvents,
      shouldHideNext: isLastDateWithMultipleEvents,
    };
  };

  const handleCorkscrewButtonClick = (
    isOpen: boolean,
    instrumentId: string,
    instrumentNarrative: string,
    eventData: OpmInputsYearDebtInstrumentCalculationResultsPeriodByEventDto[],
    eventDate: string
  ) => {
    const { shouldHidePrevious, shouldHideNext } = determineCorkscrewNavigationButtonVisibility(
      instrumentId,
      eventDate
    );
    setShouldHidePrevious(shouldHidePrevious);
    setShouldHideNext(shouldHideNext);
    setIsCorkscrewModalOpen(isOpen);
    setInstrumentId(instrumentId);
    setInstrumentNarrative(instrumentNarrative);
    setEventData(eventData);
  };

  const handleCorkscrewForecastChange = (
    instrumentId: string,
    changeType: keyof typeof ViewChangeType
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allDates: [string, any][] = [];

    allDates.push([project.valuationDate, opmInputResults.valuationDate]);

    const forecastDates = Object.entries(opmInputResults.forecastDates);
    allDates.push(...forecastDates);

    const currentEventDate = allDates.find(([_, values]) =>
      compareDeep(values.shareholderDebtInstrumentsEvents[instrumentId].periodByEvent, eventData)
    )?.[0];

    const currentEventIndex = allDates.findIndex(([date]) => date === currentEventDate);

    if (changeType === ViewChangeType.Next) {
      const nextEventIndex = allDates
        .slice(currentEventIndex + 1)
        .findIndex(
          ([, values]) =>
            values.shareholderDebtInstrumentsEvents[instrumentId].periodByEvent.length > 1
        );

      if (nextEventIndex !== -1) {
        const nextEventDate = allDates[currentEventIndex + 1 + nextEventIndex][0];
        const nextEventData =
          nextEventDate in opmInputResults.forecastDates
            ? opmInputResults.forecastDates[nextEventDate].shareholderDebtInstrumentsEvents[
                instrumentId
              ].periodByEvent
            : opmInputResults.valuationDate.shareholderDebtInstrumentsEvents[instrumentId]
                .periodByEvent;

        setEventData(nextEventData);

        const { shouldHidePrevious, shouldHideNext } = determineCorkscrewNavigationButtonVisibility(
          instrumentId,
          nextEventDate
        );
        setShouldHidePrevious(shouldHidePrevious);
        setShouldHideNext(shouldHideNext);
      }
    } else {
      const previousEventIndex = allDates
        .slice(0, currentEventIndex)
        .reverse()
        .findIndex(
          ([, values]) =>
            values.shareholderDebtInstrumentsEvents[instrumentId].periodByEvent.length > 1
        );

      if (previousEventIndex !== -1) {
        const previousEventDate = allDates[currentEventIndex - 1 - previousEventIndex][0];
        const previousEventData =
          previousEventDate in opmInputResults.forecastDates
            ? opmInputResults.forecastDates[previousEventDate].shareholderDebtInstrumentsEvents[
                instrumentId
              ].periodByEvent
            : opmInputResults.valuationDate.shareholderDebtInstrumentsEvents[instrumentId]
                .periodByEvent;
        setEventData(previousEventData);
        const { shouldHidePrevious, shouldHideNext } = determineCorkscrewNavigationButtonVisibility(
          instrumentId,
          previousEventDate
        );
        setShouldHidePrevious(shouldHidePrevious);
        setShouldHideNext(shouldHideNext);
      }
    }
  };

  const hasAnyDataToDisplay = (
    instrumentId: string,
    key: keyof OpmInputsYearDebtInstrumentCalculationResultsPeriodTotalDto
  ): boolean => {
    const hasValueInYear = (yearData: OpmInputsYearDto) =>
      Boolean(yearData?.shareholderDebtInstrumentsEvents?.[instrumentId]?.periodTotal?.[key]);

    return (
      Object.values(filteredHistoricYears).some(hasValueInYear) ||
      hasValueInYear(opmInputResults.valuationDate) ||
      caseForecasts.some(([, forecastValues]) => hasValueInYear(forecastValues))
    );
  };

  if (Object.keys(opmInputResults.instrumentInformation.instruments).length === 0) {
    return <></>;
  }

  return (
    <>
      <tr className="table-primary__row--plain">
        <th className="table-primary__cell--header">{l('_ShareholderDebtInstruments')}</th>
        <td className="table-primary__cell--header" colSpan={sectionColCount} />
      </tr>
      {Object.values(opmInputResults.instrumentInformation.instruments).map((instrument) => {
        const isInstrumentPresentAcrossYears = Object.values(opmInputResults.forecastDates).some(
          (y) => instrument.id in y.shareholderDebtInstrumentsEvents
        );
        if (!isInstrumentPresentAcrossYears) {
          return <Fragment key={instrument.id} />;
        }
        return (
          <Fragment key={instrument.id}>
            <tr className="table-primary__row--header">
              <th className="table-primary__cell--header-secondary table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                {instrument.narrative}
              </th>
              <OpmInputHistoricCells
                indexIdentifier={`${instrument.id}-corkscrew-button`}
                className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--section-start-separator table-primary__cell--section-end-separator"
                renderCell={(historicDate, historicValues) => (
                  <>
                    {historicValues.shareholderDebtInstrumentsEvents[instrument.id]?.periodByEvent
                      .length > 1 && (
                      <Button
                        className={styles['corkscrew-modal-icon']}
                        startIcon={<SvgAnalysis />}
                        onClick={() =>
                          handleCorkscrewButtonClick(
                            true,
                            instrument.id,
                            instrument.narrative,
                            historicValues.shareholderDebtInstrumentsEvents[instrument.id]
                              .periodByEvent,
                            historicDate
                          )
                        }
                        autoIconSize
                        appearance={ButtonAppearance.CLEAN}
                        size={ButtonSize.SMALL}
                      />
                    )}
                  </>
                )}
              />
              <td className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                {opmInputResults.valuationDate.shareholderDebtInstrumentsEvents[instrument.id]
                  ?.periodByEvent.length > 1 && (
                  <Button
                    className={styles['corkscrew-modal-icon']}
                    startIcon={<SvgAnalysis />}
                    onClick={() =>
                      handleCorkscrewButtonClick(
                        true,
                        instrument.id,
                        instrument.narrative,
                        opmInputResults.valuationDate.shareholderDebtInstrumentsEvents[
                          instrument.id
                        ]?.periodByEvent,
                        project.valuationDate
                      )
                    }
                    autoIconSize
                    appearance={ButtonAppearance.CLEAN}
                    size={ButtonSize.SMALL}
                  />
                )}
              </td>
              {caseForecasts.map(([forecastDate, forecastValues]) => {
                return (
                  <td
                    key={forecastDate}
                    className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                    {forecastValues.shareholderDebtInstrumentsEvents[instrument.id]?.periodByEvent
                      .length > 1 && (
                      <Button
                        className={styles['corkscrew-modal-icon']}
                        startIcon={<SvgAnalysis />}
                        onClick={() =>
                          handleCorkscrewButtonClick(
                            true,
                            instrument.id,
                            instrument.narrative,
                            forecastValues.shareholderDebtInstrumentsEvents[instrument.id]
                              .periodByEvent,
                            forecastDate
                          )
                        }
                        autoIconSize
                        appearance={ButtonAppearance.CLEAN}
                        size={ButtonSize.SMALL}
                      />
                    )}
                  </td>
                );
              })}
              <td className="table-primary__cell--header-secondary table-primary__cell--section-start-separator table-primary__cell--section-end-separator" />
            </tr>
            <ShareSectionTableRow
              title={l('_OpeningBalance')}
              valueSelector={(x) => x?.openingBalance}
              instrumentId={instrument.id}
              isBold
            />
            <ShareSectionTableRow
              title={l('_InfusionOrRedemptionNegative')}
              valueSelector={(x) => x?.additions}
              instrumentId={instrument.id}
            />
            <ShareSectionTableRow
              title={l('_AccruedInterest')}
              valueSelector={(x) => x?.accruedInterest}
              instrumentId={instrument.id}
            />
            <ShareSectionTableRow
              title={l('_ClosingBalance')}
              valueSelector={(x) => x?.closingBalance}
              instrumentId={instrument.id}
              isBold
            />
            <ShareSectionTableRow
              title={l('_CouponPc')}
              valueSelector={(x) => x?.couponPc}
              instrumentId={instrument.id}
              isPercentage
            />
            <ShareSectionTableRow
              title={l('_CouponType')}
              valueSelector={(x) =>
                mapCouponTypeInterestToTranslationWithHover(x?.couponType ?? '')?.short
              }
              toolTipLongTextSelector={(x) =>
                mapCouponTypeInterestToTranslationWithHover(x?.couponType ?? '')?.full
              }
              instrumentId={instrument.id}
              isText
            />
            {hasAnyDataToDisplay(instrument.id, 'cashPayProportionPc') && (
              <ShareSectionTableRow
                title={l('_CashPayProportion')}
                valueSelector={(x) => x?.cashPayProportionPc}
                instrumentId={instrument.id}
                isPercentage
              />
            )}
            {hasAnyDataToDisplay(instrument.id, 'cashPayInterest') && (
              <ShareSectionTableRow
                title={l('_CashInterest')}
                valueSelector={(x) => x?.cashPayInterest}
                instrumentId={instrument.id}
              />
            )}
          </Fragment>
        );
      })}
      <CorkscrewModal
        isOpen={isCorkscrewModalOpen}
        setIsOpen={setIsCorkscrewModalOpen}
        instrumentId={instrumentId}
        instrumentNarrative={instrumentNarrative}
        eventData={eventData}
        shouldHidePrevious={shouldHidePrevious}
        shouldHideNext={shouldHideNext}
        handleCorkscrewForecastChange={handleCorkscrewForecastChange}
      />
    </>
  );
};
